import { useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { useAuthStore } from "../../store/auth-store";
import YupchaIcon from "../../assets/image.png";
import YupchaTextLogo from "../../assets/Yupcha.webp";
import DashboardIcon from "../../assets/Dashboard.svg";
import SessionsIcon from "../../assets/Sessions.svg";
import ConfigurationIcon from "../../assets/Configuration.svg";
import ApiIcon from "../../assets/Api.svg";
import SettingsIcon from "../../assets/Settings.svg";
import LogoutIcon from "../../assets/Logout.svg";
import sidebarClose from "../../assets/sidebarClose.svg";
import { FiMenu, FiX } from "react-icons/fi";
import { useOrganizationStore } from "../../store/organization-store";

// --- Updated Props Type ---
interface SidebarProps {
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  isMobileMenuOpen: boolean;
  setIsMobileMenuOpen: (isOpen: boolean) => void;
}


export default function Sidebar({
  isCollapsed,
  setIsCollapsed,
  isMobileMenuOpen,
  setIsMobileMenuOpen,
}: SidebarProps) {
  const { logout } = useAuthStore();
  const { organization, fetchOrganization } = useOrganizationStore(); // <--- ADDED
  
  // track whether the collapse state was set automatically by the responsive rule
  const autoCollapsedRef = useRef(false);
  const manualOverrideRef = useRef(false);
  
  // <--- ADDED LOGO/ORG DATA LOGIC START --->
  useEffect(() => {
    fetchOrganization();
  }, [fetchOrganization]);

  const companyLogoUrl = organization?.company_logo_url;
  const organizationName = organization?.name;
  // <--- ADDED LOGO/ORG DATA LOGIC END --->
  
  useEffect(() => {
    const MD = 768;
    const LG = 1024;

    const check = () => {
      const w = window.innerWidth;
      if (w >= MD && w < LG) {
        // enter collapsed range
        if (!isCollapsed && !manualOverrideRef.current) {
          setIsCollapsed(true);
          autoCollapsedRef.current = true;
        }
      } else {
        // leaving collapsed range -> only restore if we previously auto-collapsed
        if (autoCollapsedRef.current) {
          setIsCollapsed(false);
          autoCollapsedRef.current = false;
        }
        // clear manual override whenever we leave the special range
        if (manualOverrideRef.current) {
          manualOverrideRef.current = false;
        }
      }
    };

    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [isCollapsed, setIsCollapsed]);

  // --- New function to handle link clicks on mobile ---
  const handleMobileLinkClick = () => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  const baseLinkClasses =
    "flex items-center h-11 rounded-lg transition-colors duration-200";
  const navLinkClasses = ({ isActive }: { isActive: boolean }) =>
    `${baseLinkClasses} ${
      isActive
        ? "bg-slate-800/50 text-white text-[17px]"
        : "text-gray-400 hover:text-gray-200 hover:bg-slate-800/30"
    } ${isCollapsed ? "w-11 justify-center" : "w-full px-4"}`;

  const buttonClasses = `${baseLinkClasses} text-gray-400 hover:text-gray-200 hover:bg-slate-800/30 w-full ${
    isCollapsed ? "w-11 justify-center" : "px-4"
  }`;

  return (
    <>
      {/* --- Hamburger Menu (Mobile Only) --- */}
      <button
        onClick={() => setIsMobileMenuOpen(true)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 rounded-lg backdrop-blur-sm border border-slate-700/50"
        aria-label="Open sidebar"
      >
        <FiMenu className="h-6 w-6 text-white" />
      </button>

      {/* --- Overlay (Mobile Only) --- */}
      {isMobileMenuOpen && (
        <div
          onClick={() => setIsMobileMenuOpen(false)}
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          aria-hidden="true"
        ></div>
      )}

      {/* --- Sidebar Container --- */}
      <aside
        className={`
                    bg-[#0d1025] flex flex-col border-r border-slate-800
                    h-screen
                    transition-transform duration-300 ease-in-out
                    fixed md:relative md:flex-shrink-0 z-50
                    ${
                      isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
                    } md:translate-x-0
                    ${isCollapsed ? "w-[72px]" : "w-54"}
                `}
      >
        {/* --- Logo and Toggle Section (UPDATED) --- */}
        <div className="flex  items-center h-[72px] shrink-0 px-4 border-b border-slate-800 ">
            {/* --- Logo Display Logic (UPDATED) --- */}
            {companyLogoUrl ? (
                // Company Logo
                <div className="w-8 h-8 shrink-0 flex items-center justify-center rounded-md overflow-hidden bg-white/10">
                    <img
                        src={companyLogoUrl}
                        alt="Company Logo"
                        className="w-full h-full object-contain"
                    />
                </div>
            ) : (
                // Default App Logo
                <img
                    src={YupchaIcon}
                    alt="Yupcha Icon"
                    className="w-8 h-8 shrink-0"
                />
            )}
          <div
            className={`overflow-hidden transition-all duration-200 ${
              isCollapsed ? "w-0" : "w-32 ml-2"
            }`}
          >
             {/* --- Text Display Logic (UPDATED) --- */}
            {organizationName && companyLogoUrl ? (
                // Organization Name Placeholder
                <span className="text-white font-semibold text-lg whitespace-nowrap">
                    {organizationName}
                </span>
            ) : (
                // Default App Text Logo
                <img src={YupchaTextLogo} alt="Yupcha" className="h-8" />
            )}
          </div>
          <button
            onClick={() => {
              manualOverrideRef.current = true;
              setIsCollapsed(!isCollapsed);
            }}
            className={`hidden md:block cursor-pointer shrink-0 transition-all duration-300 
                   ${isCollapsed ? "ml-[7px] " : "ml-auto"}`}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <img
              src={sidebarClose}
              alt="Toggle sidebar"
              className={`w-6 transition-transform duration-300 ${
                isCollapsed ? "rotate-180 h-5" : "h-6"
              }`}
            />
          </button>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="ml-auto md:hidden "
            aria-label="Close sidebar"
          >
            <FiX className="w-6 h-6 text-gray-400" />
          </button>
        </div>

        {/* --- Navigation Links --- */}
        <nav className="flex-1 px-3 py-6 space-y-2">
          {[
            { to: "/", icon: DashboardIcon, label: "Dashboard" },
            { to: "/sessions", icon: SessionsIcon, label: "Interviews" },
            {
              to: "/configuration",
              icon: ConfigurationIcon,
              label: "Job Templates",
            },
            { to: "/api", icon: ApiIcon, label: "API" },
          ].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={navLinkClasses}
              onClick={handleMobileLinkClick}
            >
              <img src={item.icon} alt="" className="w-5 h-5 shrink-0" />
              <div
                className={`overflow-hidden transition-all duration-200 ${
                  isCollapsed ? "w-0" : "w-full ml-3"
                }`}
              >
                <span className="whitespace-nowrap">{item.label}</span>
              </div>
            </NavLink>
          ))}
        </nav>

        {/* --- Bottom Section --- */}
        <div className="px-3 py-6 space-y-2 border-t border-slate-800">
          <NavLink
            to="/settings"
            className={navLinkClasses}
            // --- ADD THIS LINE ---
            onClick={handleMobileLinkClick}
          >
            <img src={SettingsIcon} alt="" className="w-5 h-5 shrink-0" />
            <div
              className={`overflow-hidden transition-all duration-200 ${
                isCollapsed ? "w-0" : "w-full ml-3"
              }`}
            >
              <span className="whitespace-nowrap">Settings</span>
            </div>
          </NavLink>
          <button
            onClick={() => {
              logout();
              // --- ADD THIS LINE ---
              handleMobileLinkClick();
            }}
            className={buttonClasses}
          >
            <img src={LogoutIcon} alt="" className="w-5 h-5 shrink-0" />
            <div
              className={`overflow-hidden transition-all duration-200 ${
                isCollapsed ? "w-0" : " ml-3"
              }`}
            >
              <span className="whitespace-nowrap">Logout</span>
            </div>
          </button>
        </div>
      </aside>
    </>
  );
}